import { apiClient } from '@/api/api'
import type { LookConfig, LookSubmitResult, TinderCardItem } from '../types/look-config'
import { mockLookConfig } from './mock-look-config'
import { getMockLookCard } from './mock-look-cards'

export interface LookSubmitRequest {
  lookConfigId: string
  answers: Record<string, any>
  metadata?: {
    completedAt?: string
    deviceType?: string
    language?: string
  }
}

export async function fetchLookConfig(): Promise<LookConfig> {
  try {
    const response = await apiClient.get<LookConfig>('/look/config')
    return response.data
  } catch (error) {
    // Fallback to mock config if backend is not available
    console.warn('Failed to fetch look config from backend, using mock config:', error)
    return mockLookConfig
  }
}

export async function submitLookAnswers(data: LookSubmitRequest): Promise<LookSubmitResult> {
  try {
    const response = await apiClient.post<LookSubmitResult>('/look/submit', data)
    return response.data
  } catch (error) {
    console.error('Error submitting look answers:', error)
    throw error
  }
}

export interface FetchNextLookCardParams {
  lookConfigId: string
  currentCardIndex: number
  limit: number
}

export async function fetchNextLookCard(params: FetchNextLookCardParams): Promise<TinderCardItem[]> {
  try {
    const { lookConfigId, currentCardIndex, limit } = params
    const queryParams = new URLSearchParams({
      lookConfigId,
      currentCardIndex: currentCardIndex.toString(),
      limit: limit.toString()
    })
    const response = await apiClient.get<TinderCardItem[]>(`/look/cards/next?${queryParams.toString()}`)
    return response.data
  } catch (error) {
    // Fallback to mock cards if backend is not available
    console.warn('Failed to fetch look cards from backend, using mock cards:', error)
    const mockCards: TinderCardItem[] = []
    for (let i = 0; i < params.limit; i++) {
      mockCards.push(getMockLookCard(params.currentCardIndex + i))
    }
    return mockCards
  }
}

export interface SubmitLookCardActionParams {
  lookConfigId: string
  cardId: string
  action: 'like' | 'dislike'
}

export async function submitLookCardAction(params: SubmitLookCardActionParams): Promise<void> {
  const { lookConfigId, cardId, action } = params
  const maxRetries = 3
  const retryDelay = 1000 // 1 секунда

  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      await apiClient.post(`/look/cards/${cardId}/like`, {
        lookConfigId,
        cardId,
        action
      })
      return
    } catch (error: any) {
      const isBackendUnavailable = attempt === 1 && (
        error?.response?.status === 404 ||
        error?.response?.status >= 500 ||
        !error?.response
      )

      if (isBackendUnavailable) {
        console.warn('⚠️ Backend unavailable, simulating card action submission:', {
          cardId,
          action,
          lookConfigId,
          status: error?.response?.status
        })
        await new Promise(resolve => setTimeout(resolve, 200))
        return
      }

      if (attempt === 1) {
        console.warn('Failed to submit look card action to backend, retrying...', error)
      }

      console.error(`Error submitting look card action (attempt ${attempt}/${maxRetries}):`, error)

      if (attempt === maxRetries) {
        throw new Error(`Не удалось отправить действие. Пожалуйста, попробуйте еще раз.`)
      }

      await new Promise(resolve => setTimeout(resolve, retryDelay * attempt))
    }
  }
}

